import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { 
  usePaymentStats, 
  usePaymentPlans, 
  useCreatePaymentPlan,
  useDeletePaymentPlan,
  useInstallments,
  useRecordPayment,
  useCancelPayment
} from '../lib/hooks';
import { useStudents } from '../lib/hooks';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Plus,
  Search,
  Eye,
  Trash2,
  Banknote,
  Smartphone,
  Calendar,
  X,
  Loader2,
  ChevronRight
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../lib/translations';

const formatCurrency = (amount, currency = 'XAF', lang = 'fr') => {
  return new Intl.NumberFormat(lang === 'de' ? 'de-DE' : 'fr-FR').format(amount) + ' ' + currency;
};

const formatDate = (dateString, lang = 'fr') => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(lang === 'de' ? 'de-DE' : 'fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getStatusBadge = (status, lang = 'fr') => {
  const variants = {
    pending: { label: lang === 'de' ? 'Ausstehend' : 'En attente', className: 'bg-yellow-100 text-yellow-800' },
    partial: { label: lang === 'de' ? 'Teilweise' : 'Partiel', className: 'bg-blue-100 text-blue-800' },
    paid: { label: lang === 'de' ? 'Bezahlt' : 'Payé', className: 'bg-green-100 text-green-800' },
    overdue: { label: lang === 'de' ? 'Überfällig' : 'En retard', className: 'bg-red-100 text-red-800' },
  };
  const variant = variants[status] || variants.pending;
  return <Badge className={variant.className}>{variant.label}</Badge>;
};

// Create Payment Plan Sheet Component
const CreatePlanSheet = ({ open, onOpenChange, onSuccess }) => {
  const { lang } = useLanguage();
  const [formData, setFormData] = useState({
    student_id: '',
    total_amount: '',
    currency: 'XAF',
    description: '',
    installments: [{ amount: '', due_date: '' }]
  });
  
  const { data: studentsData, isLoading: studentsLoading } = useStudents({ per_page: 1000 });
  const createPlan = useCreatePaymentPlan();
  
  const students = studentsData?.students || [];
  
  const handleAddInstallment = () => {
    setFormData(prev => ({
      ...prev,
      installments: [...prev.installments, { amount: '', due_date: '' }]
    }));
  };
  
  const handleRemoveInstallment = (index) => {
    setFormData(prev => ({
      ...prev,
      installments: prev.installments.filter((_, i) => i !== index)
    }));
  };
  
  const handleInstallmentChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      installments: prev.installments.map((inst, i) => 
        i === index ? { ...inst, [field]: value } : inst
      )
    }));
  };
  
  const calculateTotal = () => {
    return formData.installments.reduce((sum, inst) => sum + (parseInt(inst.amount) || 0), 0);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.student_id) {
      toast.error(lang === 'de' ? 'Bitte wählen Sie eine/n Studierende/n aus' : 'Veuillez sélectionner un étudiant');
      return;
    }
    
    const total = calculateTotal();
    if (total <= 0) {
      toast.error(lang === 'de' ? 'Der Gesamtbetrag muss größer als 0 sein' : 'Le montant total doit être supérieur à 0');
      return;
    }
    
    const validInstallments = formData.installments.filter(
      inst => inst.amount && inst.due_date
    );
    
    if (validInstallments.length === 0) {
      toast.error(lang === 'de' ? 'Fügen Sie mindestens eine gültige Rate hinzu' : 'Ajoutez au moins une échéance valide');
      return;
    }
    
    try {
      await createPlan.mutateAsync({
        student_id: formData.student_id,
        total_amount: total,
        currency: formData.currency,
        description: formData.description || null,
        installments: validInstallments.map(inst => ({
          amount: parseInt(inst.amount),
          due_date: inst.due_date
        }))
      });
      
      toast.success(lang === 'de' ? 'Zahlungsplan erfolgreich erstellt' : 'Plan de paiement créé avec succès');
      setFormData({
        student_id: '',
        total_amount: '',
        currency: 'XAF',
        description: '',
        installments: [{ amount: '', due_date: '' }]
      });
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'de' ? 'Fehler beim Erstellen' : 'Erreur lors de la création'));
    }
  };
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{lang === 'de' ? 'Zahlungsplan erstellen' : 'Créer un plan de paiement'}</SheetTitle>
          <SheetDescription>
            {lang === 'de' ? 'Legen Sie die Zahlungsraten für eine/n Studierende/n fest' : 'Définissez les échéances de paiement pour un étudiant'}
          </SheetDescription>
        </SheetHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          <div className="space-y-2">
            <Label htmlFor="student">{lang === 'de' ? 'Studierende/r' : 'Étudiant'} *</Label>
            <Select 
              value={formData.student_id} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, student_id: value }))}
            >
              <SelectTrigger data-testid="student-select">
                <SelectValue placeholder={lang === 'de' ? 'Studierende/n auswählen' : 'Sélectionner un étudiant'} />
              </SelectTrigger>
              <SelectContent>
                {studentsLoading ? (
                  <SelectItem value="loading" disabled>{lang === 'de' ? 'Laden...' : 'Chargement...'}</SelectItem>
                ) : (
                  students.map(student => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.first_name} {student.last_name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="currency">{lang === 'de' ? 'Währung' : 'Devise'}</Label>
            <Select 
              value={formData.currency} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="XAF">XAF (Franc CFA)</SelectItem>
                <SelectItem value="EUR">EUR (Euro)</SelectItem>
                <SelectItem value="USD">USD (Dollar)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">{lang === 'de' ? 'Beschreibung (optional)' : 'Description (optionnel)'}</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={lang === 'de' ? 'Z.B.: Studiengebühren 2025' : 'Ex: Frais de scolarité 2025'}
              rows={2}
            />
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>{lang === 'de' ? 'Raten' : 'Échéances'} *</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={handleAddInstallment}
              >
                <Plus className="w-4 h-4 mr-1" />
                {lang === 'de' ? 'Hinzufügen' : 'Ajouter'}
              </Button>
            </div>
            
            {formData.installments.map((inst, index) => (
              <div key={index} className="flex gap-2 items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">{lang === 'de' ? 'Betrag' : 'Montant'}</Label>
                  <Input
                    type="number"
                    placeholder={lang === 'de' ? 'Betrag' : 'Montant'}
                    value={inst.amount}
                    onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)}
                    data-testid={`installment-amount-${index}`}
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">{lang === 'de' ? 'Fällig am' : 'Échéance'}</Label>
                  <Input
                    type="date"
                    value={inst.due_date}
                    onChange={(e) => handleInstallmentChange(index, 'due_date', e.target.value)}
                    data-testid={`installment-date-${index}`}
                  />
                </div>
                {formData.installments.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveInstallment(index)}
                    className="text-destructive"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium">
                {lang === 'de' ? 'Gesamt' : 'Total'}: {formatCurrency(calculateTotal(), formData.currency)}
              </p>
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="w-full bg-primary hover:bg-primary/90"
            disabled={createPlan.isPending}
            data-testid="create-plan-submit"
          >
            {createPlan.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {lang === 'de' ? 'Plan erstellen' : 'Créer le plan'}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
};

// Plan Detail Dialog Component
const PlanDetailDialog = ({ plan, open, onOpenChange }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'resp_admin';
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    payment_method: 'cash',
    paid_amount: '',
    reference: '',
    notes: ''
  });
  
  const { data: installments, isLoading } = useInstallments(open ? plan?.id : null);
  const recordPayment = useRecordPayment(plan?.id);
  const cancelPayment = useCancelPayment(plan?.id);
  
  const handleRecordPayment = async () => {
    if (!paymentForm.paid_amount || parseInt(paymentForm.paid_amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }
    
    try {
      await recordPayment.mutateAsync({
        installmentId: selectedInstallment.id,
        data: {
          payment_method: paymentForm.payment_method,
          paid_amount: parseInt(paymentForm.paid_amount),
          reference: paymentForm.reference || null,
          notes: paymentForm.notes || null
        }
      });
      
      toast.success('Paiement enregistré');
      setRecordPaymentOpen(false);
      setPaymentForm({ payment_method: 'cash', paid_amount: '', reference: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };
  
  const handleCancelPayment = async (installmentId) => {
    if (!confirm('Êtes-vous sûr de vouloir annuler ce paiement ?')) return;
    
    try {
      await cancelPayment.mutateAsync(installmentId);
      toast.success('Paiement annulé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };
  
  if (!plan) return null;
  
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails du plan de paiement</DialogTitle>
            <DialogDescription>
              {plan.student_name} - {plan.description || 'Plan de paiement'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="border-0 bg-muted/50">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Total</p>
                  <p className="text-lg font-bold">{formatCurrency(plan.total_amount, plan.currency)}</p>
                </CardContent>
              </Card>
              <Card className="border-0 bg-green-50">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-green-600 mb-1">{t('payments_paid', lang)}</p>
                  <p className="text-lg font-bold text-green-700">{formatCurrency(plan.paid_amount, plan.currency)}</p>
                </CardContent>
              </Card>
              <Card className="border-0 bg-orange-50">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-orange-600 mb-1">{lang === 'de' ? 'Ausstehend' : 'Restant'}</p>
                  <p className="text-lg font-bold text-orange-700">{formatCurrency(plan.remaining_amount, plan.currency)}</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Installments Table */}
            <div>
              <h4 className="font-medium mb-3">Échéances ({plan.paid_installments}/{plan.installments_count})</h4>
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{lang === 'de' ? 'Fällig am' : 'Échéance'}</TableHead>
                      <TableHead>{t('payments_amount', lang)}</TableHead>
                      <TableHead>{t('students_status', lang)}</TableHead>
                      <TableHead>Méthode</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {installments?.map((inst) => (
                      <TableRow key={inst.id}>
                        <TableCell>{formatDate(inst.due_date)}</TableCell>
                        <TableCell>{formatCurrency(inst.amount, plan.currency)}</TableCell>
                        <TableCell>{getStatusBadge(inst.status)}</TableCell>
                        <TableCell>
                          {inst.status === 'paid' ? (
                            <span className="flex items-center gap-1 text-sm">
                              {inst.payment_method === 'cash' ? (
                                <><Banknote className="w-4 h-4" /> Espèces</>
                              ) : (
                                <><Smartphone className="w-4 h-4" /> Mobile</>
                              )}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {isAdmin ? (
                            inst.status === 'paid' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => handleCancelPayment(inst.id)}
                                disabled={cancelPayment.isPending}
                                data-testid={`cancel-payment-${inst.id}`}
                              >
                                Annuler
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedInstallment(inst);
                                  setPaymentForm(prev => ({
                                    ...prev,
                                    paid_amount: inst.amount.toString()
                                  }));
                                  setRecordPaymentOpen(true);
                                }}
                                data-testid={`record-payment-${inst.id}`}
                              >
                                <Banknote className="w-4 h-4 mr-1" />
                                Enregistrer
                              </Button>
                            )
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Record Payment Dialog */}
      <Dialog open={recordPaymentOpen} onOpenChange={setRecordPaymentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('payments_record_payment', lang)}</DialogTitle>
            <DialogDescription>
              Échéance du {selectedInstallment && formatDate(selectedInstallment.due_date)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{lang === 'de' ? 'Zahlungsmethode' : 'Méthode de paiement'}</Label>
              <Select 
                value={paymentForm.payment_method}
                onValueChange={(value) => setPaymentForm(prev => ({ ...prev, payment_method: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">
                    <span className="flex items-center gap-2">
                      <Banknote className="w-4 h-4" /> Espèces
                    </span>
                  </SelectItem>
                  <SelectItem value="mobile_money">
                    <span className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4" /> Mobile Money
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Montant payé *</Label>
              <Input
                type="number"
                value={paymentForm.paid_amount}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, paid_amount: e.target.value }))}
                placeholder="Montant"
                data-testid="payment-amount-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Référence (optionnel)</Label>
              <Input
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                placeholder="N° de transaction"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Remarques..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordPaymentOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleRecordPayment}
              disabled={recordPayment.isPending}
              className="bg-primary hover:bg-primary/90"
              data-testid="confirm-payment-btn"
            >
              {recordPayment.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmer le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Main Payments Page Component
const PaymentsPage = () => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const isStudent = user?.role === 'student';
  
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  const { data: stats, isLoading: statsLoading } = usePaymentStats();
  const { data: plans, isLoading: plansLoading } = usePaymentPlans({ 
    search: search || undefined,
    status: statusFilter || undefined 
  });
  const deletePlan = useDeletePaymentPlan();
  
  const handleDeletePlan = async (planId) => {
    if (!confirm(lang === 'de' ? 'Sind Sie sicher, dass Sie diesen Zahlungsplan löschen möchten?' : 'Êtes-vous sûr de vouloir supprimer ce plan de paiement ?')) return;
    
    try {
      await deletePlan.mutateAsync(planId);
      toast.success(lang === 'de' ? 'Plan gelöscht' : 'Plan supprimé');
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'de' ? 'Fehler beim Löschen' : 'Erreur lors de la suppression'));
    }
  };
  
  const handleViewPlan = (plan) => {
    setSelectedPlan(plan);
    setDetailOpen(true);
  };
  
  return (
    <div className="space-y-6 animate-fade-in" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">
            {isStudent ? (lang === 'de' ? 'Meine Zahlungen' : 'Mes paiements') : (lang === 'de' ? 'Zahlungen' : 'Paiements')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isStudent ? (lang === 'de' ? 'Verfolgen Sie Ihre Zahlungen und Fälligkeiten' : 'Suivez vos paiements et échéances') : (lang === 'de' ? 'Verwalten Sie die Zahlungspläne der Studierenden' : 'Gérez les plans de paiement des étudiants')}
          </p>
        </div>
        {!isStudent && (
          <Button 
            onClick={() => setCreatePlanOpen(true)}
            className="bg-primary hover:bg-primary/90"
            data-testid="create-plan-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {lang === 'de' ? 'Neuer Plan' : 'Nouveau plan'}
          </Button>
        )}
      </div>

      {/* Stat Cards */}
      {!isStudent && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{lang === 'de' ? 'Erwartet gesamt' : 'Total attendu'}</p>
                  <p className="text-2xl font-bold font-heading">
                    {statsLoading ? '-' : formatCurrency(stats?.total_expected || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-secondary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{lang === 'de' ? 'Eingenommen' : 'Encaissé'}</p>
                  <p className="text-2xl font-bold font-heading text-green-600">
                    {statsLoading ? '-' : formatCurrency(stats?.total_paid || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t('status_pending', lang)}</p>
                  <p className="text-2xl font-bold font-heading text-yellow-600">
                    {statsLoading ? '-' : formatCurrency(stats?.total_pending || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t('payments_overdue', lang)}</p>
                  <p className="text-2xl font-bold font-heading text-red-600">
                    {statsLoading ? '-' : formatCurrency(stats?.total_overdue || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={lang === 'de' ? 'Nach Studierendenname suchen...' : "Rechercher par nom d'étudiant..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder={lang === 'de' ? 'Alle Status' : 'Tous les statuts'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{lang === 'de' ? 'Alle Status' : 'Tous les statuts'}</SelectItem>
                <SelectItem value="pending">{lang === 'de' ? 'Ausstehend' : 'En attente'}</SelectItem>
                <SelectItem value="partial">{lang === 'de' ? 'Teilweise' : 'Partiel'}</SelectItem>
                <SelectItem value="paid">{lang === 'de' ? 'Bezahlt' : 'Payé'}</SelectItem>
                <SelectItem value="overdue">{lang === 'de' ? 'Überfällig' : 'En retard'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Plans Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-medium">{lang === 'de' ? 'Zahlungspläne' : 'Plans de paiement'}</CardTitle>
        </CardHeader>
        <CardContent>
          {plansLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : plans?.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading text-xl font-semibold mb-2">{lang === 'de' ? 'Keine Zahlungspläne' : 'Aucun plan de paiement'}</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                {isStudent 
                  ? (lang === 'de' ? 'Ihnen wurde noch kein Zahlungsplan zugewiesen.' : "Vous n'avez pas encore de plan de paiement assigné.")
                  : (lang === 'de' ? 'Erstellen Sie Ihren ersten Zahlungsplan, um die Zahlungen der Studierenden zu verfolgen.' : "Créez votre premier plan de paiement pour commencer à suivre les paiements des étudiants.")}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{lang === 'de' ? 'Studierende/r' : 'Étudiant'}</TableHead>
                  <TableHead>{lang === 'de' ? 'Gesamt' : 'Total'}</TableHead>
                  <TableHead>{lang === 'de' ? 'Bezahlt' : 'Payé'}</TableHead>
                  <TableHead>{lang === 'de' ? 'Raten' : 'Échéances'}</TableHead>
                  <TableHead>{lang === 'de' ? 'Status' : 'Statut'}</TableHead>
                  <TableHead>{lang === 'de' ? 'Erstellt am' : 'Créé le'}</TableHead>
                  <TableHead className="text-right">{lang === 'de' ? 'Aktionen' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans?.map((plan) => (
                  <TableRow key={plan.id} data-testid={`plan-row-${plan.id}`}>
                    <TableCell className="font-medium">{plan.student_name}</TableCell>
                    <TableCell>{formatCurrency(plan.total_amount, plan.currency)}</TableCell>
                    <TableCell className="text-green-600">
                      {formatCurrency(plan.paid_amount, plan.currency)}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {plan.paid_installments}/{plan.installments_count}
                      </span>
                    </TableCell>
                    <TableCell>{getStatusBadge(plan.status, lang)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(plan.created_at, lang)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewPlan(plan)}
                          data-testid={`view-plan-${plan.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {!isStudent && user?.role === 'admin' && plan.paid_installments === 0 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDeletePlan(plan.id)}
                            disabled={deletePlan.isPending}
                            data-testid={`delete-plan-${plan.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Plan Sheet */}
      <CreatePlanSheet 
        open={createPlanOpen} 
        onOpenChange={setCreatePlanOpen}
        onSuccess={() => {}}
      />
      
      {/* Plan Detail Dialog */}
      <PlanDetailDialog 
        plan={selectedPlan}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </div>
  );
};

export default PaymentsPage;
